'use client';
import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const categories = ['All', 'Web App', 'Mobile', 'Design', 'E-Commerce', 'Cloud'];

const projects = [
  {
    id: '1',
    title: 'Lumière Beauty — Web App for Salon',
    category: 'Web App',
    description:
      'Premium Salon and Parlour web app with an integrated appointment and booking system.',
    image:
      'https://akif-khan.vercel.app/assets/images/parlor-ak-img.jpg?auto=format&fit=crop&w=800&q=80',
    imageAlt: 'Book your luxury beauty experience at Lumière Beauty.',
    tags: ['Next.js', 'React', 'Tailwind CSS', 'TypeScript', 'Framer Motion', 'Supabase', 'Vercel'],
    color: '#6C63FF',
    metrics: '200+ Users',
    featured: true,
    url: 'https://parlor-ak.vercel.app',
  },
  {
    id: '2',
    title: 'Gulahmed — Elite E-commerce Suite',
    category: 'E-Commerce',
    description:
      'Premium e-commerce platform for seamless shopping, fast performance and a refined user experience.',
    image:
      'https://akif-khan.vercel.app/assets/images/ecom-ak-img.jpg?auto=format&fit=crop&w=800&q=80',
    imageAlt: 'Ecommerce Shopify Website',
    tags: ['Liquid', 'Core JS', 'Shopify', 'Lazy Sizez', 'Masonry', 'JQuery', 'AWS'],
    color: '#10D9A0',
    metrics: '1000+ Products',
    featured: true,
    url: 'https://www.gulahmedshop.com',
  },
  {
    id: '3',
    title: 'Vape Store — Online Retail Vape Shop',
    category: 'E-Commerce',
    description: 'High-end e-commerce platform with personalized discovery and seamless checkout.',

    image:
      'https://akif-khan.vercel.app/assets/images/ecom-ak-img2.jpg?auto=format&fit=crop&w=800&q=80',
    imageAlt: 'Online Retail Vape Shop',
    tags: ['Cloudflare', 'Underscore JS', 'Woocommerce', 'MySQL', 'JQuery', 'Yoast', 'Wordpress'],
    color: '#F59E0B',
    metrics: 'Google Top Ranking',
    featured: false,
    url: 'https://www.vapestore.pk',
  },
  {
    id: '4',
    title: 'ZAG International Pvt. Ltd. — WebApp',
    category: 'Web App',
    description:
      'A premium business consultancy web app built for performance, scalability, and advanced features.',
    image:
      'https://akif-khan.vercel.app/assets/images/ak-proj-zag-img2.jpg?auto=format&fit=crop&w=800&q=80',
    imageAlt: 'React Web App ZAG International',
    tags: ['Three.JS', 'Lucide', 'TailwindCSS', 'Framer Motion', 'Typescript', 'React', 'NextJS'],
    color: '#00D4FF',
    metrics: '10+ Companies',
    featured: true,
    url: 'https://www.zag.com.pk',
  },
  {
    id: '5',
    title: 'Maheen Karim — Custom Online Store',
    category: 'E-commerce',
    description: 'High-end e-commerce platform delivering fast, seamless, and refined shopping.',
    image:
      'https://akif-khan.vercel.app/assets/images/ak-proj-mk-img2.jpg?auto=format&fit=crop&w=800&q=80',
    imageAlt: 'Premium Shopify Store',
    tags: [
      'HTML',
      'CSS',
      'Javascript',
      'Liquid',
      'GraphQL',
      'Rest API',
      'Shopify',
      'Ruby on Rails',
    ],
    color: '#A855F7',
    metrics: 'International Sales',
    featured: false,
    url: 'https://maheenkarim.com.pk',
  },
  {
    id: '6',
    title: 'VIP Vape Store — Premium Online Shop',
    category: 'E-Commerce',
    description:
      'Advanced E-commerce solution built for smooth shopping, speed, and a refined digital experience.',
    image:
      'https://akif-khan.vercel.app/assets/images/ecom-ak-img3.jpg?auto=format&fit=crop&w=800&q=80',
    imageAlt: 'Professional E-commerce system',
    tags: ['Shopify', 'HSTS', 'Priority Hints', 'Open Graph', 'Core JS', 'Cloudflare', 'Judge Me'],
    color: '#FF6B6B',
    metrics: '40 Countries',
    featured: false,
    url: 'https://vipvapestore.pk',
  },
];

export default function PortfolioSection() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const filtered =
    activeFilter === 'All' ? projects : projects?.filter((p) => p?.category === activeFilter);

  return (
    <section id="portfolio" className="py-16 lg:py-20 relative overflow-hidden">
      {/* Background Glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 50% at 80% 50%, rgba(0,212,255,0.04) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 initially-hidden animate-in">
          <div className="section-label mb-3">Selected Work</div>
          <h2
            className="text-4xl md:text-6xl font-extrabold mb-4"
            style={{ color: 'var(--text-primary)', letterSpacing: '-0.04em' }}
          >
            Portfolio & <span className="gradient-text-amber">Case Studies</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Real products. Real impact. Trusted by top brands worldwide.
          </p>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 justify-center mb-12 initially-hidden animate-in">
          {categories?.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              className={`filter-tab ${activeFilter === cat ? 'active' : ''}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered?.map((project, i) => (
            <div
              key={project?.id}
              className={`portfolio-card glass-card initially-hidden animate-in ${
                project?.featured && i === 0 ? 'md:col-span-2 lg:col-span-1' : ''
              }`}
              style={{ animationDelay: `${i * 0.1}s` }}
              onMouseEnter={() => setHoveredId(project?.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {/* Image Container */}
              <div className="relative overflow-hidden group" style={{ height: '220px' }}>
                <AppImage
                  src={project?.image}
                  alt={project?.imageAlt}
                  fill
                  className="object-cover transition-transform duration-700"
                  style={{
                    transform: hoveredId === project?.id ? 'scale(1.08)' : 'scale(1)',
                  }}
                />

                {/* Overlay */}
                <div
                  className="absolute inset-0 transition-opacity duration-400"
                  style={{
                    background: `linear-gradient(to top, rgba(8,11,20,0.95) 0%, rgba(8,11,20,0.2) 60%, transparent 100%)`,
                    opacity: hoveredId === project?.id ? 1 : 0.6,
                  }}
                />

                {/* Badges */}
                <div
                  className="absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-bold glass z-20"
                  style={{ color: project?.color, border: `1px solid ${project?.color}40` }}
                >
                  {project?.metrics}
                </div>
                <div
                  className="absolute top-4 left-4 px-3 py-1.5 rounded-full text-xs font-semibold z-20"
                  style={{
                    background: `${project?.color}20`,
                    color: project?.color,
                    border: `1px solid ${project?.color}30`,
                  }}
                >
                  {project?.category}
                </div>

                {/* Hover Action Link */}
                <div
                  className="absolute inset-0 flex items-center justify-center transition-all duration-400 z-30"
                  style={{
                    opacity: hoveredId === project?.id ? 1 : 0,
                    transform: hoveredId === project?.id ? 'translateY(0)' : 'translateY(10px)',
                  }}
                >
                  <a
                    href={project?.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary flex items-center gap-2 text-sm no-underline"
                    style={{ padding: '12px 28px' }}
                  >
                    <Icon name="EyeIcon" size={16} />
                    <span>View Project</span>
                  </a>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3
                  className="text-lg font-bold mb-2"
                  style={{ color: 'var(--text-primary)', letterSpacing: '-0.02em' }}
                >
                  {project?.title}
                </h3>
                <p
                  className="text-sm leading-relaxed mb-4"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {project?.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {project?.tags?.map((tag) => (
                    <span
                      key={tag}
                      className="text-xs font-mono px-2.5 py-1 rounded-lg"
                      style={{
                        background: `${project?.color}12`,
                        color: project?.color,
                        border: `1px solid ${project?.color}20`,
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View all CTA */}
        <div className="text-center mt-12 initially-hidden animate-in">
          <button className="btn-ghost flex items-center gap-2 mx-auto transition-transform hover:translate-x-1">
            <span>View All Projects</span>
            <Icon name="ArrowRightIcon" size={18} />
          </button>
        </div>
      </div>
    </section>
  );
}
